import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarCheck, ChevronLeft, ChevronRight, Clock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  BOOKING_PATH_PREFIX,
  BookingApiError,
  bookSlot,
  bookingWorkspaceSlug,
  fetchBookingPage,
  fetchSlots,
  type BookRequest,
  type BookedMeeting,
  type PublicBookingPage,
} from "./bookingApi";
import { TurnstileWidget } from "./TurnstileWidget";

/**
 * The public booking page - <workspace>.obviouscrm.com/meeting/<handle>.
 * Rendered by App before any workspace/auth bootstrap: visitors aren't CRM
 * users. Times are shown in the visitor's own zone; the services API owns
 * every rule (hours, notice, what's busy) and re-checks on booking.
 */

const DAY = 86_400_000;
const WEEK = 7 * DAY;

const visitorTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

const handleFromPath = () =>
  decodeURIComponent(
    window.location.pathname.slice(BOOKING_PATH_PREFIX.length).split("/")[0] ??
      "",
  );

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const BookingPage = () => {
  const slug = bookingWorkspaceSlug();
  const handle = handleFromPath();
  const timeZone = useMemo(visitorTimeZone, []);

  const [page, setPage] = useState<
    PublicBookingPage | null | "missing" | "error"
  >(null);
  const [weekStart, setWeekStart] = useState(startOfToday);
  const [slots, setSlots] = useState<string[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [booked, setBooked] = useState<
    (BookedMeeting & { email: string }) | null
  >(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || !handle) {
      setPage("missing");
      return;
    }
    fetchBookingPage(slug, handle)
      .then(setPage)
      .catch((e) =>
        setPage(
          e instanceof BookingApiError && e.status === 404
            ? "missing"
            : "error",
        ),
      );
  }, [slug, handle]);

  const loadSlots = useCallback(() => {
    if (!slug || typeof page !== "object" || !page) return;
    setSlots(null);
    fetchSlots(slug, handle, weekStart, new Date(weekStart.getTime() + WEEK))
      .then(setSlots)
      .catch(() => {
        setSlots([]);
        setNotice("Couldn't load open times. Please refresh to try again.");
      });
  }, [slug, handle, page, weekStart]);

  useEffect(loadSlots, [loadSlots]);

  if (page === null) return <Shell />;
  if (page === "missing" || page === "error") {
    return (
      <Shell>
        <p className="text-center text-muted-foreground py-10">
          {page === "missing"
            ? "This booking page isn't available. Check the link with the person who sent it."
            : "Something went wrong loading this page. Please try again in a moment."}
        </p>
      </Shell>
    );
  }

  const lastBookable = startOfToday().getTime() + page.maxDaysAhead * DAY;

  return (
    <Shell>
      <header className="mb-5">
        <p className="text-sm text-muted-foreground">{page.hostName}</p>
        <h1 className="text-2xl font-semibold">{page.title}</h1>
        <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
          <Clock className="w-4 h-4" /> {page.durationMinutes} min
        </p>
      </header>

      {booked ? (
        <Confirmation meeting={booked} timeZone={timeZone} />
      ) : selected ? (
        <BookingForm
          page={page}
          start={selected}
          timeZone={timeZone}
          onBack={() => setSelected(null)}
          onBooked={(m) => setBooked(m)}
          onTaken={() => {
            setSelected(null);
            setNotice("Sorry - that time was just taken. Please pick another.");
            loadSlots();
          }}
          book={(body) => bookSlot(slug!, handle, body)}
        />
      ) : (
        <>
          {notice && (
            <p className="mb-4 rounded-md bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100 px-3 py-2 text-sm">
              {notice}
            </p>
          )}
          <WeekPicker
            weekStart={weekStart}
            canGoBack={weekStart.getTime() > startOfToday().getTime()}
            canGoForward={weekStart.getTime() + WEEK < lastBookable}
            onChange={(d) => {
              setNotice(null);
              setWeekStart(d);
            }}
          />
          <SlotList
            slots={slots}
            timeZone={timeZone}
            onPick={(s) => {
              setNotice(null);
              setSelected(s);
            }}
          />
          <p className="text-xs text-muted-foreground mt-4">
            Times shown in {timeZone ?? "your time zone"}.
          </p>
        </>
      )}
    </Shell>
  );
};

const Shell = ({ children }: { children?: React.ReactNode }) => (
  <div className="min-h-screen bg-muted/40 px-4 py-8 sm:py-14">
    <Card className="max-w-2xl mx-auto">
      <CardContent className="py-2">{children}</CardContent>
    </Card>
    <p className="text-center text-xs text-muted-foreground mt-4">
      Scheduling by Obvious
    </p>
  </div>
);

const dayFmt = (timeZone?: string) =>
  new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
  });
const timeFmt = (timeZone?: string) =>
  new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  });
const fullFmt = (timeZone?: string) =>
  new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

const WeekPicker = ({
  weekStart,
  canGoBack,
  canGoForward,
  onChange,
}: {
  weekStart: Date;
  canGoBack: boolean;
  canGoForward: boolean;
  onChange: (d: Date) => void;
}) => {
  const end = new Date(weekStart.getTime() + WEEK - DAY);
  const fmt = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  });
  return (
    <div className="flex items-center justify-between mb-4">
      <Button
        variant="outline"
        size="icon"
        aria-label="Earlier dates"
        disabled={!canGoBack}
        onClick={() =>
          onChange(
            new Date(
              Math.max(startOfToday().getTime(), weekStart.getTime() - WEEK),
            ),
          )
        }
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <span className="text-sm font-medium">
        {fmt.format(weekStart)} - {fmt.format(end)}
      </span>
      <Button
        variant="outline"
        size="icon"
        aria-label="Later dates"
        disabled={!canGoForward}
        onClick={() => onChange(new Date(weekStart.getTime() + WEEK))}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

const SlotList = ({
  slots,
  timeZone,
  onPick,
}: {
  slots: string[] | null;
  timeZone?: string;
  onPick: (slot: string) => void;
}) => {
  if (slots === null)
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        Loading open times...
      </p>
    );
  if (slots.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        No open times this week - try the next one.
      </p>
    );
  }
  const days = new Map<string, string[]>();
  const fmt = dayFmt(timeZone);
  for (const s of slots) {
    const key = fmt.format(new Date(s));
    days.set(key, [...(days.get(key) ?? []), s]);
  }
  const time = timeFmt(timeZone);
  return (
    <div className="flex flex-col gap-4">
      {[...days].map(([day, times]) => (
        <section key={day}>
          <h2 className="text-sm font-medium mb-2">{day}</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {times.map((s) => (
              <Button
                key={s}
                variant="outline"
                size="sm"
                onClick={() => onPick(s)}
              >
                {time.format(new Date(s))}
              </Button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

const BookingForm = ({
  page,
  start,
  timeZone,
  onBack,
  onBooked,
  onTaken,
  book,
}: {
  page: PublicBookingPage;
  start: string;
  timeZone?: string;
  onBack: () => void;
  onBooked: (m: BookedMeeting & { email: string }) => void;
  onTaken: () => void;
  book: (body: BookRequest) => Promise<BookedMeeting>;
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [website, setWebsite] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsCaptcha = !!page.turnstileSiteKey;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const meeting = await book({
        start,
        name,
        email,
        notes: notes || undefined,
        timeZone,
        website,
        captchaToken: captchaToken ?? undefined,
      });
      onBooked({ ...meeting, email });
    } catch (err) {
      const reason = err instanceof BookingApiError ? err.reason : "error";
      if (reason === "unavailable") return onTaken();
      setError(
        reason === "invalid"
          ? "Please check your name and email."
          : reason === "captcha"
            ? "Please complete the verification and try again."
            : reason === "rate_limited"
              ? "Too many bookings from here - please try again later."
              : "Couldn't book that time. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <p className="rounded-md bg-muted px-3 py-2 text-sm font-medium">
        {fullFmt(timeZone).format(new Date(start))}
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="booking-name">Your name</Label>
        <Input
          id="booking-name"
          required
          maxLength={120}
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="booking-email">Email</Label>
        <Input
          id="booking-email"
          type="email"
          required
          maxLength={254}
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="booking-notes">
          Anything to share before the meeting? (optional)
        </Label>
        <Textarea
          id="booking-notes"
          maxLength={2000}
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      {/* Honeypot: hidden from people, filled by bots. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
      />
      {needsCaptcha && (
        <TurnstileWidget
          siteKey={page.turnstileSiteKey!}
          onToken={setCaptchaToken}
        />
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2 justify-between">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button
          type="submit"
          disabled={submitting || (needsCaptcha && !captchaToken)}
        >
          {submitting ? "Booking..." : "Confirm booking"}
        </Button>
      </div>
    </form>
  );
};

const Confirmation = ({
  meeting,
  timeZone,
}: {
  meeting: BookedMeeting & { email: string };
  timeZone?: string;
}) => (
  <div className="flex flex-col items-center text-center gap-2 py-6">
    <CalendarCheck className="w-10 h-10 text-primary" />
    <h2 className="text-xl font-semibold">You're booked</h2>
    <p>
      {meeting.title} with {meeting.hostName}
    </p>
    <p className="font-medium">
      {fullFmt(timeZone).format(new Date(meeting.start))}
    </p>
    <p className="text-sm text-muted-foreground mt-2">
      A calendar invite is on its way to {meeting.email}.
    </p>
  </div>
);
